import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.ts';
import { ItemCard } from '../components/ItemCard.tsx';
import type { Item } from '../lib/types.ts';

const STATUS_FILTERS = [
  { label: 'Analysed', value: 'analysed' },
  { label: 'Flagged', value: 'flagged' },
  { label: 'All', value: '' },
  { label: 'Dismissed', value: 'dismissed' },
];

const CATEGORY_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Economic', value: 'economic' },
  { label: 'Business', value: 'business' },
  { label: 'Property', value: 'property' },
  { label: 'Finance', value: 'finance' },
  { label: 'EU', value: 'eu' },
  { label: 'Grants', value: 'grants' },
  { label: 'Insolvency', value: 'insolvency' },
];

interface InboxProps {
  onOpenItem?: (id: string) => void;
}

export function Inbox({ onOpenItem }: InboxProps) {
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [scoreMin, setScoreMin] = useState(3);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.items.list({ status, category, score_min: scoreMin || undefined, limit: LIMIT, offset });
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [status, category, scoreMin, offset]);

  useEffect(() => { load(); }, [load]);

  function handleStatusChange(id: string, newStatus: string) {
    if (status && newStatus !== status) {
      setItems(prev => prev.filter(i => i.id !== id));
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Insights Inbox</h1>
        <span style={{ color: '#999', fontSize: 13 }}>{total} items</span>
        <button onClick={load} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px' }}>↻ Refresh</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(f => (
          <button key={f.value} onClick={() => { setStatus(f.value); setOffset(0); }}
            style={filterBtn(status === f.value)}>
            {f.label}
          </button>
        ))}
        <span style={{ color: '#ddd' }}>|</span>
        {CATEGORY_FILTERS.map(f => (
          <button key={f.value} onClick={() => { setCategory(f.value); setOffset(0); }}
            style={filterBtn(category === f.value)}>
            {f.label}
          </button>
        ))}
        <span style={{ color: '#ddd' }}>|</span>
        <label style={{ fontSize: 12, color: '#555' }}>
          Min score:&nbsp;
          <input type="number" min="0" max="10" value={scoreMin}
            onChange={e => { setScoreMin(Number(e.target.value)); setOffset(0); }}
            style={{ width: 40, fontSize: 12 }} />
        </label>
      </div>

      {loading && <p style={{ color: '#999' }}>Loading…</p>}

      {!loading && items.length === 0 && (
        <p style={{ color: '#999', fontStyle: 'italic' }}>No items match the current filters.</p>
      )}

      {items.map(item => (
        <ItemCard key={item.id} item={item} onStatusChange={handleStatusChange} onOpen={onOpenItem} />
      ))}

      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>← Prev</button>
          <span style={{ fontSize: 12, color: '#666', alignSelf: 'center' }}>
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <button disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function filterBtn(active: boolean): React.CSSProperties {
  return {
    background: active ? '#111' : '#f0f0f0',
    color: active ? '#fff' : '#555',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
    fontWeight: active ? 700 : 400,
  };
}
